import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom'; // Import Link
import { getMarketAnalysis, saveMarketAnalysisResult, getSavedMarketAnalysisResult } from '../api'; // Import the new API function
import Tooltip from './Tooltip';
import { showConfirm, showAlert } from '../utils/ModalService';


const MarketAnalysis: React.FC = () => {
    const { propertyId } = useParams<{ propertyId: string }>();
    const [analysis, setAnalysis] = useState<any>(null);
    const [structuredMarketAnalysis, setStructuredMarketAnalysis] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedAnalysis, setSavedAnalysis] = useState<any>(null); // New state for saved analysis

    const fetchAnalysis = async () => {
        if (!propertyId) return;
        setLoading(true);
        setError(null);

        try {
            const response = await getMarketAnalysis({ property_id: propertyId });
            console.log("Market Analysis API Response:", response);
            setAnalysis(response); // Set the entire response, which includes property_id and market_analysis

            if (response.structured_market_analysis) {
                setStructuredMarketAnalysis(response.structured_market_analysis);
            } else if (response.market_commentary) {
                const trimmed = response.market_commentary.trim();
                if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.includes('```json')) {
                    try {
                        let jsonString = trimmed.replace(/^```json\s*|\s*```$/g, '');
                        const parsedCommentary = JSON.parse(jsonString);
                        setStructuredMarketAnalysis(parsedCommentary);
                    } catch (jsonError) {
                        console.warn("Error parsing market commentary JSON:", jsonError);
                        setStructuredMarketAnalysis({ market_summary: response.market_commentary });
                    }
                } else {
                    setStructuredMarketAnalysis({ market_summary: response.market_commentary });
                }
            }
        } catch (err: any) {
            if (err.response && err.response.data && err.response.data.detail) {
                const detail = err.response.data.detail;
                setError(typeof detail === 'object' ? JSON.stringify(detail) : detail);
            } else {
                setError('An unexpected error occurred while fetching the market analysis.');
            }
            console.error(err);
        }

        setLoading(false);
    };

    const handleSaveAnalysis = async () => {
        if (!analysis || !propertyId) return;
        try {
            const inputParameters = { propertyId: propertyId }; // Define input parameters
            await saveMarketAnalysisResult(propertyId, {
                property_id: propertyId,
                input_parameters: inputParameters,
                generated_result: analysis
            });
            setSavedAnalysis(analysis); // Update savedAnalysis state
            showAlert('Market analysis saved successfully!');
        } catch (err) {
            console.error('Error saving market analysis:', err);
            showAlert('Failed to save market analysis.');
        }
    };

    const handleGetAnalysis = (e: React.FormEvent) => {
        e.preventDefault();
        fetchAnalysis();
    };

    useEffect(() => {
        const loadSavedAnalysis = async () => {
            if (!propertyId) return;
            setLoading(true);
            try {
                const saved = await getSavedMarketAnalysisResult(propertyId);
                if (saved) {
                    setSavedAnalysis(saved);
                    setAnalysis(saved); // Also set analysis to the saved one for display
                    if (saved.structured_market_analysis) {
                        setStructuredMarketAnalysis(saved.structured_market_analysis);
                    } else if (saved.market_commentary) {
                        const trimmed = saved.market_commentary.trim();
                        if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.includes('```json')) {
                            try {
                                let jsonString = trimmed.replace(/^```json\s*|\s*```$/g, '');
                                const parsedCommentary = JSON.parse(jsonString);
                                setStructuredMarketAnalysis(parsedCommentary);
                            } catch (jsonError) {
                                console.warn("Error parsing saved market commentary JSON:", jsonError);
                                setStructuredMarketAnalysis({ market_summary: saved.market_commentary });
                            }
                        } else {
                            setStructuredMarketAnalysis({ market_summary: saved.market_commentary });
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading saved market analysis:', err);
            } finally {
                setLoading(false);
            }
        };
        loadSavedAnalysis();
    }, [propertyId]);

    const displayAnalysis = savedAnalysis || analysis;

    return (
        <div className="container mx-auto p-4">
            <Link to="/properties" className="text-primary hover:underline mb-4 inline-block">{t("marketAnalysis.k1")}</Link>
                        <h1 className="text-2xl font-bold mb-4 flex items-center">
                            {t("marketAnalysis.k2")} <Tooltip text="Market Analysis provides an in-depth evaluation of a property's market conditions, including comparable sales data, local economic indicators, and key market trends. This tool helps users understand the competitive landscape and potential investment opportunities. This Market Analysis is generated by an AI model based on available data. It provides estimates and insights and should not be considered definitive financial or investment advice. Always consult with a qualified professional before making any investment decisions." />
                        </h1>

            <form onSubmit={handleGetAnalysis} className="mb-4">
                <button type="submit" className="bg-primary text-white p-2 rounded" disabled={loading}>
                    {loading ? t("marketAnalysis.k3") : t("marketAnalysis.k4")}
                </button>
                {displayAnalysis && !loading && (
                    <button type="button" onClick={handleSaveAnalysis} className="ml-2 bg-primary text-white p-2 rounded">
                        {t("marketAnalysis.k5")} </button>
                )}
            </form>

            {loading && <p>{t("marketAnalysis.k6")}</p>}
            {error && <p className="text-danger">{error}</p>}

            {displayAnalysis && displayAnalysis.subject_property && (
                <div className="border p-4 rounded mt-4">
                    <h2 className="text-xl font-bold mb-2">{t("marketAnalysis.k7")} {displayAnalysis.subject_property?.address || propertyId}</h2>
                    
                    <h3 className="text-lg font-semibold mt-4 mb-2">{t("marketAnalysis.k8")}</h3>
                    <p><strong>{t("marketAnalysis.k9")}</strong> {displayAnalysis.subject_property.address}, {displayAnalysis.subject_property.city}, {displayAnalysis.subject_property.state}</p>
                            <p><strong>{t("marketAnalysis.k10")}</strong> {displayAnalysis.subject_property.property_type}</p>
                            <p><strong>{t("marketAnalysis.k11")}</strong> {displayAnalysis.subject_property.square_footage}</p>

                    <h3 className="text-lg font-semibold mt-4 mb-2">{t("marketAnalysis.k12")}</h3>
                    <p><strong>{t("marketAnalysis.k13")}</strong> {displayAnalysis.comparable_properties_count}</p>
                    <p><strong>{t("marketAnalysis.k14")}</strong> ${displayAnalysis.average_price_per_sqft_comparables.toFixed(2)}</p>
                    <p><strong>{t("marketAnalysis.k15")}</strong> {displayAnalysis.formatted_average_sale_price_comparables}</p>
                    <p><strong>{t("marketAnalysis.k16")}</strong> {displayAnalysis.average_square_footage_comparables}</p>

                    {structuredMarketAnalysis && (
                        <div className="mt-4">
                            <h3 className="text-lg font-semibold">{t("marketAnalysis.k17")}</h3>
                            {structuredMarketAnalysis.market_summary && (
                                <p><strong>{t("marketAnalysis.k18")}</strong> {structuredMarketAnalysis.market_summary}</p>
                            )}
                            {structuredMarketAnalysis.key_trends && structuredMarketAnalysis.key_trends.length > 0 && (
                                <div className="mt-2">
                                    <p><strong>{t("marketAnalysis.k19")}</strong></p>
                                    <ul className="list-disc list-inside ml-4">
                                        {structuredMarketAnalysis.key_trends.map((trend: string, index: number) => (
                                            <li key={index}>{trend}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {structuredMarketAnalysis.recommendation && (
                                <p className="mt-2"><strong>{t("marketAnalysis.k20")}</strong> {structuredMarketAnalysis.recommendation}</p>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MarketAnalysis;
